import { useCallback, useRef } from "react";

const AudioContextClass = (typeof window !== "undefined")
  ? (window.AudioContext || window.webkitAudioContext)
  : null;

export function useSoftSounds() {
  const ctxRef = useRef(null);

  const ensureContext = useCallback(() => {
    if (!AudioContextClass) return null;
    if (!ctxRef.current) {
      ctxRef.current = new AudioContextClass();
    }
    if (ctxRef.current.state === "suspended") {
      ctxRef.current.resume();
    }
    return ctxRef.current;
  }, []);

  const playTone = useCallback((opts = {}) => {
    const ctx = ensureContext();
    if (!ctx) return;
    const {
      type = "triangle",
      freqStart = 440,
      freqEnd = freqStart,
      duration = 0.15,
      gain = 0.15,
      timeOffset = 0,
      attack = 0.008,
    } = opts;

    const startTime = ctx.currentTime + timeOffset;
    const endTime = startTime + duration;
    const osc = ctx.createOscillator();
    const gainNode = ctx.createGain();

    const safeStart = Math.max(40, freqStart);
    const safeEnd = Math.max(40, freqEnd);
    osc.type = type;
    osc.frequency.setValueAtTime(safeStart, startTime);
    if (safeEnd !== safeStart) {
      const ramp = type === "sine" ? "linearRampToValueAtTime" : "exponentialRampToValueAtTime";
      osc.frequency[ramp](safeEnd, endTime);
    }

    gainNode.gain.setValueAtTime(0.0001, startTime);
    gainNode.gain.linearRampToValueAtTime(gain, startTime + attack);
    gainNode.gain.exponentialRampToValueAtTime(0.0001, endTime);

    osc.connect(gainNode);
    gainNode.connect(ctx.destination);

    osc.start(startTime);
    osc.stop(endTime + 0.03);
  }, [ensureContext]);

  const playTilePlace = useCallback(() => {
    const base = 420 + Math.random() * 90;
    playTone({
      type: "triangle",
      freqStart: base,
      freqEnd: base - 110,
      duration: 0.11,
      gain: 0.12,
    });
  }, [playTone]);

  const playTileLift = useCallback(() => {
    const base = 340 + Math.random() * 70;
    playTone({
      type: "triangle",
      freqStart: base - 90,
      freqEnd: base + 80,
      duration: 0.12,
      gain: 0.12,
    });
  }, [playTone]);

  const playShuffle = useCallback(() => {
    for (let i = 0; i < 3; i++) {
      playTone({
        type: "square",
        freqStart: 180 + i * 60 + Math.random() * 30,
        freqEnd: 260 + i * 70,
        duration: 0.08,
        gain: 0.09,
        timeOffset: i * 0.045,
      });
    }
  }, [playTone]);

  const playConfirm = useCallback(() => {
    playTone({
      type: "sine",
      freqStart: 520,
      freqEnd: 680,
      duration: 0.22,
      gain: 0.15,
    });
    playTone({
      type: "sine",
      freqStart: 660,
      freqEnd: 880,
      duration: 0.18,
      gain: 0.12,
      timeOffset: 0.16,
    });
  }, [playTone]);

  const playSoftError = useCallback(() => {
    playTone({
      type: "sine",
      freqStart: 320,
      freqEnd: 180,
      duration: 0.22,
      gain: 0.12,
    });
  }, [playTone]);

  return {
    playTilePlace,
    playTileLift,
    playShuffle,
    playConfirm,
    playSoftError,
  };
}
